import { useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import type { SiteRow } from "../data/routes";
import {
  clearSiteBoundary,
  fetchSiteBoundaries,
  parseGeoJsonPolygon,
  saveSiteBoundary,
  type SiteBoundary,
} from "../data/site-boundary";
import { useReadModel } from "../data/useReadModel";

// PRD-001Y Y2 — site property boundary editor (Setup). Import/paste a GeoJSON Polygon to set the
// one active boundary per site, or clear it. The boundary then renders as the L0 layer on the CAD
// Live map. v1 is GeoJSON import (full MapLibre draw is a deferred follow-up). Writes go through
// the schedule.manage-gated hosted RPCs; viewers see the read-only boundary status.
export function SiteBoundaryEditor({
  client,
  accountId,
  canManage = false,
  pickerSites,
}: {
  client: SupabaseClient;
  accountId: string;
  /** schedule.manage — import/clear affordances only exist when true (fail closed). */
  canManage?: boolean;
  pickerSites: readonly SiteRow[];
}) {
  const { status, data, error, reload } = useReadModel<SiteBoundary[]>(
    () => fetchSiteBoundaries(client, accountId),
    [client, accountId],
  );
  const boundaries = useMemo(() => data ?? [], [data]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>("");
  const [geojsonText, setGeojsonText] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  const activeSiteId = selectedSiteId.length > 0 ? selectedSiteId : (pickerSites[0]?.siteId ?? "");
  const boundaryForSite = boundaries.find((boundary) => boundary.siteId === activeSiteId) ?? null;

  function resetFeedback(): void {
    setFailure(null);
    setReceipt(null);
  }

  async function importAndSave(): Promise<void> {
    if (busy || !canManage) return;
    resetFeedback();
    if (activeSiteId.length === 0) {
      setFailure("Pick a site first.");
      return;
    }
    const parsed = parseGeoJsonPolygon(geojsonText);
    if (!parsed.ok) {
      setFailure(parsed.error);
      return;
    }
    setBusy(true);
    const outcome = await saveSiteBoundary(client, accountId, activeSiteId, parsed.ring, {
      source: "import_geojson",
    });
    setBusy(false);
    if (outcome.ok) {
      setGeojsonText("");
      setReceipt("Site boundary saved.");
      reload();
    } else {
      setFailure(outcome.reason);
    }
  }

  async function clear(): Promise<void> {
    if (busy || !canManage || boundaryForSite === null) return;
    resetFeedback();
    setBusy(true);
    const outcome = await clearSiteBoundary(client, accountId, activeSiteId);
    setBusy(false);
    if (outcome.ok) {
      setReceipt("Site boundary cleared.");
      reload();
    } else {
      setFailure(outcome.reason);
    }
  }

  if (pickerSites.length === 0) {
    return (
      <p className="surface__state" role="status">
        Add a site in step 1–2 first; property boundaries attach to a site.
      </p>
    );
  }

  return (
    <div className="operator-panel site-boundary">
      <label className="tpl__field site-boundary__site">
        Site
        <select
          aria-label="Site for property boundary"
          value={activeSiteId}
          onChange={(event) => {
            setSelectedSiteId(event.target.value);
            resetFeedback();
          }}
        >
          {pickerSites.map((site) => (
            <option key={site.siteId} value={site.siteId}>
              {site.siteName}
            </option>
          ))}
        </select>
      </label>

      <p className="site-boundary__status" role="status">
        {status === "loading" && data === null
          ? "Loading boundaries…"
          : status === "error"
            ? (error ?? "Could not load site boundaries.")
            : boundaryForSite !== null
              ? `Active boundary: ${boundaryForSite.label} (${boundaryForSite.ring.length} points, ${boundaryForSite.source}).`
              : "No property boundary set for this site yet."}
      </p>

      {canManage ? (
        <>
          <label className="tpl__field">
            Paste a GeoJSON polygon (Polygon, Feature, or FeatureCollection)
            <textarea
              className="site-boundary__geojson"
              value={geojsonText}
              onChange={(event) => setGeojsonText(event.target.value)}
              placeholder='{"type":"Polygon","coordinates":[[[-85.76,38.25],[-85.75,38.25],[-85.75,38.26],[-85.76,38.25]]]}'
              rows={4}
            />
          </label>
          <div className="policy__editor-actions">
            <button
              type="button"
              className="minter__confirm-go"
              disabled={busy}
              onClick={() => void importAndSave()}
            >
              {busy ? "Saving…" : "Import & save boundary"}
            </button>
            {boundaryForSite !== null ? (
              <button type="button" className="builder__cancel" disabled={busy} onClick={() => void clear()}>
                Clear boundary
              </button>
            ) : null}
          </div>
          <p className="page__note">
            One active boundary per site. Saving replaces the current one. The boundary shows as the
            muted property layer on the Command Center map.
          </p>
        </>
      ) : (
        <p className="page__note">Read-only for this role.</p>
      )}

      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </div>
  );
}
